// /crm/hooks/useBoardColumns.js
import { useEffect, useRef, useState } from "react";
import b from "../../../services/api/Boards";

const getColumnLeadCount = (source, fallback = null) => {
  const candidateKeys = [
    "leadCount",
    "count",
    "total",
    "totalCount",
    "itemsCount",
    "leadsCount",
  ];

  for (const key of candidateKeys) {
    const value = source?.[key];
    if (Number.isFinite(Number(value))) {
      return Number(value);
    }
  }

  return fallback;
};

export function useBoardColumns({ questId, filters, shareToken = "", searchQuery = "" }) {
  const [columns, setColumns] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [boardReady, setBoardReady] = useState(false);
  const [boardMeta, setBoardMeta] = useState(null);

  const [columnPage, setColumnPage] = useState({});
  const [columnHasMore, setColumnHasMore] = useState({});
  const [columnLoading, setColumnLoading] = useState({});
  const columnRequests = useRef(new Map());
  const activeColumnRequests = useRef({});

  const resetState = () => {
    setColumns({});
    setColumnOrder([]);
    setColumnPage({});
    setColumnHasMore({});
    setBoardMeta(null);
    activeColumnRequests.current = {};
  };

  const loadBoard = async () => {
    setBoardReady(false);

    const res = await b.getBoard(questId, undefined, undefined, shareToken);
    const board = res?.data?.board;
    setBoardMeta(board || null);

    if (!board?.columns) {
      setBoardReady(true);
      return;
    }

    const cols = {};
    for (const id of Object.keys(board.columns)) {
      cols[id] = {
        ...board.columns[id],
        items: [],
        leadCount: getColumnLeadCount(board.columns[id], 0),
      };
    }

    setColumns(cols);
    setColumnOrder(Object.keys(cols));
    setBoardReady(true);

    // lazy load
    for (const id of Object.keys(cols)) {
      loadColumn(id, true, searchQuery);
    }
  };

  const loadColumn = async (colId, reset = false, search = "") => {
    const skip = reset ? 0 : columnPage[colId] || 0;
    const limit = 30;
    const { startDate, endDate } = filters.getBackendDates();
    const requestKey = JSON.stringify({
      questId,
      colId,
      skip,
      search: search.trim(),
      startDate,
      endDate,
      callFilters: filters.callFilters,
      sourceFilter: filters.sourceFilter,
    });
    const existingRequest = columnRequests.current.get(requestKey);

    if (existingRequest) return existingRequest;

    activeColumnRequests.current[colId] = requestKey;
    setColumnLoading(prev => ({ ...prev, [colId]: true }));

    const request = (async () => {
      const res = await b.getColumnLeads(
        questId,
        colId,
        skip,
        limit,
        search.trim(),
        startDate,
        endDate,
        shareToken,
        filters.callFilters,
        filters.sourceFilter
      );

      if (activeColumnRequests.current[colId] !== requestKey) return;

      const incoming = res?.data?.items || [];
      const hasMore = !!res?.data?.hasMore;
      const responseLeadCount = getColumnLeadCount(res?.data);

      setColumns(prev => ({
        ...prev,
        [colId]: {
          ...prev[colId],
          items: reset ? incoming : [...(prev[colId]?.items || []), ...incoming],
          leadCount:
            responseLeadCount ??
            prev[colId]?.leadCount ??
            (reset ? incoming.length : (prev[colId]?.items || []).length + incoming.length),
        },
      }));

      setColumnPage(prev => ({ ...prev, [colId]: skip + limit }));
      setColumnHasMore(prev => ({ ...prev, [colId]: hasMore }));
      setColumnLoading(prev => ({ ...prev, [colId]: false }));
    })();

    columnRequests.current.set(requestKey, request);

    try {
      return await request;
    } finally {
      columnRequests.current.delete(requestKey);
    }
  };

  useEffect(() => {
    if (!questId) return;
    resetState();
    loadBoard();
  }, [questId, shareToken]);

  return {
    columns,
    setColumns, 
    columnOrder,
    setColumnOrder,
    boardMeta,
    boardReady,
    loadColumn,
    loadBoard,
    columnLoading,
    columnHasMore
  };
}
